import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Toggle } from '../components/ui/Toggle';
import { Search, Plus, Package, Edit, Trash2, ChevronDown, ChevronRight, ChevronLeft, ImageOff, CheckCircle, Clock, XCircle, Layers, Star, X, Copy, Globe, Lock, Unlock } from 'lucide-react';
import { levelConfig } from '../data/optionsData';
import { initialGlobalOptions, initialCatalogs } from '../data/optionsData';
import type { GlobalOption } from '../data/optionsData';
import { CreateOptionModal } from '../components/options/CreateOptionModal';
// --- Types ---
type PackageOption = {
  id: string;
  category: string;
  name: string;
  image: string;
};
type PackageTier = {
  id: string;
  name: string;
  level: 'standard' | 'level-1' | 'level-2' | 'level-3';
  description: string;
  price: number;
  originalPrice: number;
  options: PackageOption[];
};
type BuilderPackage = {
  id: string;
  name: string;
  builderId: string;
  status: 'active' | 'draft' | 'disabled';
  scope: 'global' | 'room';
  room: string;
  allowCustomization: boolean;
  tiers: PackageTier[];
};
const ROOM_OPTIONS = ['Kitchen', 'Bathroom', 'Living Room', 'Bedroom', 'Basement', 'Exterior', 'Smart Home', 'General'];
// --- Seed Data ---
const seedPackages: BuilderPackage[] = [{
  id: 'pkg-1',
  name: 'Kitchen Packages',
  builderId: 'mattamy',
  status: 'active',
  scope: 'room',
  room: 'Kitchen',
  allowCustomization: true,
  tiers: [{
    id: 'tier-0',
    name: 'Standard',
    level: 'standard',
    description: 'Included kitchen features at no extra cost',
    price: 0,
    originalPrice: 0,
    options: [{
      id: 'opt-1',
      category: 'Refrigerator',
      name: 'Top Freezer',
      image: ''
    }, {
      id: 'opt-2',
      category: 'Range Oven',
      name: 'Electric Freestanding',
      image: ''
    }, {
      id: 'opt-3',
      category: 'Dishwasher',
      name: 'Standard Wash',
      image: ''
    }, {
      id: 'opt-4',
      category: 'Microwave',
      name: 'Countertop',
      image: ''
    }, {
      id: 'opt-5',
      category: 'Countertop',
      name: 'Laminate',
      image: ''
    }]
  }, {
    id: 'tier-1',
    name: 'Package 1',
    level: 'level-1',
    description: 'Upgraded stainless steel finish and better efficiency',
    price: 5900,
    originalPrice: 6700,
    options: [{
      id: 'opt-6',
      category: 'Refrigerator',
      name: 'Side-by-Side',
      image: 'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg'
    }, {
      id: 'opt-7',
      category: 'Range Oven',
      name: 'Stainless Steel',
      image: ''
    }, {
      id: 'opt-8',
      category: 'Dishwasher',
      name: 'Ultra Quiet',
      image: ''
    }, {
      id: 'opt-9',
      category: 'Microwave',
      name: 'Over-the-Range',
      image: ''
    }, {
      id: 'opt-10',
      category: 'Countertop',
      name: 'Butcher Block',
      image: ''
    }]
  }, {
    id: 'tier-2',
    name: 'Package 2',
    level: 'level-2',
    description: 'Professional style with premium features',
    price: 9200,
    originalPrice: 10250,
    options: [{
      id: 'opt-11',
      category: 'Refrigerator',
      name: 'French Door 36"',
      image: 'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg'
    }, {
      id: 'opt-12',
      category: 'Range Oven',
      name: 'Professional',
      image: ''
    }, {
      id: 'opt-13',
      category: 'Dishwasher',
      name: 'Third Rack',
      image: ''
    }, {
      id: 'opt-14',
      category: 'Microwave',
      name: 'Built-In',
      image: ''
    }, {
      id: 'opt-15',
      category: 'Countertop',
      name: 'Granite',
      image: ''
    }]
  }, {
    id: 'tier-3',
    name: 'Package 3',
    level: 'level-3',
    description: 'Luxury chef kitchen with top-tier appliances',
    price: 14500,
    originalPrice: 16800,
    options: [{
      id: 'opt-16',
      category: 'Refrigerator',
      name: 'Built-In Column',
      image: ''
    }, {
      id: 'opt-17',
      category: 'Range Oven',
      name: '48" Dual Fuel',
      image: ''
    }, {
      id: 'opt-18',
      category: 'Dishwasher',
      name: 'Panel Ready',
      image: ''
    }, {
      id: 'opt-19',
      category: 'Microwave',
      name: 'Speed Oven',
      image: ''
    }, {
      id: 'opt-20',
      category: 'Countertop',
      name: 'Calacatta Marble',
      image: ''
    }]
  }]
}, {
  id: 'pkg-2',
  name: 'Smart Home Packages',
  builderId: 'mattamy',
  status: 'draft',
  scope: 'room',
  room: 'Smart Home',
  allowCustomization: false,
  tiers: [{
    id: 'tier-s0',
    name: 'Standard',
    level: 'standard',
    description: 'Basic connectivity included',
    price: 0,
    originalPrice: 0,
    options: [{
      id: 'opt-21',
      category: 'Thermostat',
      name: 'Programmable',
      image: ''
    }, {
      id: 'opt-22',
      category: 'Doorbell',
      name: 'Standard Chime',
      image: ''
    }]
  }, {
    id: 'tier-s1',
    name: 'Tech Package 1',
    level: 'level-1',
    description: 'Essential smart home features',
    price: 1500,
    originalPrice: 1800,
    options: [{
      id: 'opt-23',
      category: 'Thermostat',
      name: 'Smart Thermostat',
      image: ''
    }, {
      id: 'opt-24',
      category: 'Doorbell',
      name: 'Video Doorbell',
      image: ''
    }, {
      id: 'opt-25',
      category: 'Lock',
      name: 'Smart Deadbolt',
      image: ''
    }]
  }]
}];
const statusConfig: Record<BuilderPackage['status'], {
  label: string;
  variant: 'success' | 'error' | 'neutral';
  icon: typeof CheckCircle;
}> = {
  active: {
    label: 'Active',
    variant: 'success',
    icon: CheckCircle
  },
  disabled: {
    label: 'Disabled',
    variant: 'error',
    icon: XCircle
  },
  draft: {
    label: 'Draft',
    variant: 'neutral',
    icon: Clock
  }
};
const levelOptions: PackageTier['level'][] = ['standard', 'level-1', 'level-2', 'level-3'];
const genId = () => Math.random().toString(36).slice(2, 9);
// ─── Modal Overlay ─────────────────────────────────────────────────────────────
function ModalOverlay({
  children,
  onClose
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-6241">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" data-id="element-6242">
        {children}
      </div>
    </div>;
}
function ModalHeader({
  title,
  onClose
}: {
  title: string;
  onClose: () => void;
}) {
  return <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-6243">
      <h2 className="text-base font-semibold text-slate-900" data-id="element-6244">{title}</h2>
      <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-6245">
        <X className="h-4 w-4" data-id="element-6246" />
      </button>
    </div>;
}
// ─── Create Package Modal ──────────────────────────────────────────────────────
function CreatePackageModal({
  builderId,
  onClose,
  onCreate
}: {
  builderId: string;
  onClose: () => void;
  onCreate: (pkg: BuilderPackage) => void;
}) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<BuilderPackage['status']>('draft');
  const [scope, setScope] = useState<'global' | 'room'>('room');
  const [room, setRoom] = useState(ROOM_OPTIONS[0]);
  const [allowCustomization, setAllowCustomization] = useState(true);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Package name is required');
      return;
    }
    onCreate({
      id: genId(),
      name: name.trim(),
      builderId,
      status,
      scope,
      room: scope === 'room' ? room : '',
      allowCustomization,
      tiers: []
    });
    onClose();
  };
  return <ModalOverlay onClose={onClose} data-id="element-6247">
      <ModalHeader title="Create Package" onClose={onClose} data-id="element-6248" />
      <div className="px-6 py-5 space-y-4" data-id="element-6249">
        <Input label="Package Name" placeholder="e.g. Kitchen Packages, Flooring Packages" value={name} onChange={e => {
        setName(e.target.value);
        setError('');
      }} error={error} data-id="element-6250" />

        {/* Scope */}
        <div className="space-y-2" data-id="element-6251">
          <label className="block text-sm font-medium text-slate-700" data-id="element-6252">
            Package Scope
          </label>
          <div className="flex gap-2" data-id="element-6253">
            <button type="button" onClick={() => setScope('room')} className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${scope === 'room' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-id="element-6254">
              <div className="font-medium" data-id="element-6255">Room-Specific</div>
              <div className="text-[11px] mt-0.5 opacity-70" data-id="element-6256">
                Applies to a single room
              </div>
            </button>
            <button type="button" onClick={() => setScope('global')} className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${scope === 'global' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-id="element-6257">
              <div className="font-medium" data-id="element-6258">Global</div>
              <div className="text-[11px] mt-0.5 opacity-70" data-id="element-6259">
                Whole house, all categories
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3" data-id="element-6260">
          {scope === 'room' && <div className="space-y-1.5" data-id="element-6261">
              <label className="block text-sm font-medium text-slate-700" data-id="element-6262">
                Room / Category
              </label>
              <select className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" value={room} onChange={e => setRoom(e.target.value)} data-id="element-6263">
                {ROOM_OPTIONS.map(r => <option key={r} value={r} data-id="element-6264">
                    {r}
                  </option>)}
              </select>
            </div>}
          <div className={`space-y-1.5 ${scope === 'global' ? 'col-span-2' : ''}`} data-id="element-6265">
            <label className="block text-sm font-medium text-slate-700" data-id="element-6266">
              Status
            </label>
            <select className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" value={status} onChange={e => setStatus(e.target.value as BuilderPackage['status'])} data-id="element-6267">
              <option value="draft" data-id="element-6268">Draft</option>
              <option value="active" data-id="element-6269">Active</option>
              <option value="disabled" data-id="element-6270">Disabled</option>
            </select>
          </div>
        </div>

        {/* Customization toggle */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-6271">
          <div data-id="element-6272">
            <p className="text-sm font-medium text-slate-700" data-id="element-6273">
              Allow Customization
            </p>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-6274">
              {allowCustomization ? 'Buyers can override individual options after selecting this package' : 'Package is locked — buyers cannot change individual options'}
            </p>
          </div>
          <Toggle checked={allowCustomization} onChange={setAllowCustomization} size="sm" data-id="element-6275" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50" data-id="element-6276">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-6277">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-6278">
          Create Package
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Edit Package Modal ────────────────────────────────────────────────────────
function EditPackageModal({
  pkg,
  onClose,
  onSave
}: {
  pkg: BuilderPackage;
  onClose: () => void;
  onSave: (updates: Partial<BuilderPackage>) => void;
}) {
  const [name, setName] = useState(pkg.name);
  const [status, setStatus] = useState(pkg.status);
  const [scope, setScope] = useState<'global' | 'room'>(pkg.scope || 'room');
  const [room, setRoom] = useState(pkg.room || ROOM_OPTIONS[0]);
  const [allowCustomization, setAllowCustomization] = useState(pkg.allowCustomization ?? true);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Package name is required');
      return;
    }
    onSave({
      name: name.trim(),
      status,
      scope,
      room: scope === 'room' ? room : '',
      allowCustomization
    });
    onClose();
  };
  return <ModalOverlay onClose={onClose} data-id="element-6279">
      <ModalHeader title="Edit Package" onClose={onClose} data-id="element-6280" />
      <div className="px-6 py-5 space-y-4" data-id="element-6281">
        <Input label="Package Name" value={name} onChange={e => {
        setName(e.target.value);
        setError('');
      }} error={error} data-id="element-6282" />

        {/* Scope */}
        <div className="space-y-2" data-id="element-6283">
          <label className="block text-sm font-medium text-slate-700" data-id="element-6284">
            Package Scope
          </label>
          <div className="flex gap-2" data-id="element-6285">
            <button type="button" onClick={() => setScope('room')} className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${scope === 'room' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-id="element-6286">
              <div className="font-medium" data-id="element-6287">Room-Specific</div>
              <div className="text-[11px] mt-0.5 opacity-70" data-id="element-6288">
                Applies to a single room
              </div>
            </button>
            <button type="button" onClick={() => setScope('global')} className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${scope === 'global' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-id="element-6289">
              <div className="font-medium" data-id="element-6290">Global</div>
              <div className="text-[11px] mt-0.5 opacity-70" data-id="element-6291">
                Whole house, all categories
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3" data-id="element-6292">
          {scope === 'room' && <div className="space-y-1.5" data-id="element-6293">
              <label className="block text-sm font-medium text-slate-700" data-id="element-6294">
                Room / Category
              </label>
              <select className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" value={room} onChange={e => setRoom(e.target.value)} data-id="element-6295">
                {ROOM_OPTIONS.map(r => <option key={r} value={r} data-id="element-6296">
                    {r}
                  </option>)}
              </select>
            </div>}
          <div className={`space-y-1.5 ${scope === 'global' ? 'col-span-2' : ''}`} data-id="element-6297">
            <label className="block text-sm font-medium text-slate-700" data-id="element-6298">
              Status
            </label>
            <select className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" value={status} onChange={e => setStatus(e.target.value as BuilderPackage['status'])} data-id="element-6299">
              <option value="draft" data-id="element-6300">Draft</option>
              <option value="active" data-id="element-6301">Active</option>
              <option value="disabled" data-id="element-6302">Disabled</option>
            </select>
          </div>
        </div>

        {/* Customization toggle */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-6303">
          <div data-id="element-6304">
            <p className="text-sm font-medium text-slate-700" data-id="element-6305">
              Allow Customization
            </p>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-6306">
              {allowCustomization ? 'Buyers can override individual options after selecting this package' : 'Package is locked — buyers cannot change individual options'}
            </p>
          </div>
          <Toggle checked={allowCustomization} onChange={setAllowCustomization} size="sm" data-id="element-6307" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50" data-id="element-6308">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-6309">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-6310">
          Save Changes
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Add Tier Modal ────────────────────────────────────────────────────────────
function AddTierModal({
  packageName,
  onClose,
  onAdd
}: {
  packageName: string;
  onClose: () => void;
  onAdd: (tier: Omit<PackageTier, 'id' | 'options'>) => void;
}) {
  const [name, setName] = useState('');
  const [level, setLevel] = useState<PackageTier['level']>('level-1');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
    price?: string;
  }>({});
  const handleSubmit = () => {
    const errs: {
      name?: string;
      price?: string;
    } = {};
    if (!name.trim()) errs.name = 'Tier name is required';
    if (!isStandard && (price === '' || isNaN(Number(price)) || Number(price) < 0)) errs.price = 'Valid price is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onAdd({
      name: name.trim(),
      level,
      description: description.trim(),
      price: isStandard ? 0 : Number(price),
      originalPrice: isStandard ? 0 : Number(originalPrice) || Number(price)
    });
    onClose();
  };
  const isStandard = level === 'standard';
  return <ModalOverlay onClose={onClose} data-id="element-6311">
      <ModalHeader title={`Add Tier to "${packageName}"`} onClose={onClose} data-id="element-6312" />
      <div className="px-6 py-5 space-y-4" data-id="element-6313">
        <div className="grid grid-cols-2 gap-3" data-id="element-6314">
          <Input label="Tier Name" placeholder="e.g. Package 1" value={name} onChange={e => {
          setName(e.target.value);
          setErrors(p => ({
            ...p,
            name: undefined
          }));
        }} error={errors.name} data-id="element-6315" />
          <div className="space-y-1.5" data-id="element-6316">
            <label className="block text-sm font-medium text-slate-700" data-id="element-6317">
              Level
            </label>
            <select className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" value={level} onChange={e => setLevel(e.target.value as PackageTier['level'])} data-id="element-6318">
              {levelOptions.map(l => <option key={l} value={l} data-id="element-6319">
                  {levelConfig[l]?.label || l}
                </option>)}
            </select>
          </div>
        </div>
        <div className="space-y-1.5" data-id="element-6320">
          <label className="block text-sm font-medium text-slate-700" data-id="element-6321">
            Description
          </label>
          <textarea className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 placeholder:text-slate-400" rows={2} placeholder="Brief description of what's included..." value={description} onChange={e => setDescription(e.target.value)} data-id="element-6322" />
        </div>
        {!isStandard && <div className="grid grid-cols-2 gap-3" data-id="element-6323">
            <Input label="Price ($)" type="number" placeholder="0" value={price} onChange={e => {
          setPrice(e.target.value);
          setErrors(p => ({
            ...p,
            price: undefined
          }));
        }} error={errors.price} data-id="element-6324" />
            <Input label="Original Price ($)" type="number" placeholder="0" value={originalPrice} onChange={e => setOriginalPrice(e.target.value)} data-id="element-6325" />
          </div>}
        {isStandard && <p className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-id="element-6326">
            Standard tier is included at no extra cost — price will be $0.
          </p>}
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50" data-id="element-6327">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-6328">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-6329">
          Add Tier
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Edit Tier Modal ───────────────────────────────────────────────────────────
function EditTierModal({
  tier,
  onClose,
  onSave
}: {
  tier: PackageTier;
  onClose: () => void;
  onSave: (updates: Partial<PackageTier>) => void;
}) {
  const [name, setName] = useState(tier.name);
  const [level, setLevel] = useState(tier.level);
  const [description, setDescription] = useState(tier.description);
  const [price, setPrice] = useState(String(tier.price));
  const [originalPrice, setOriginalPrice] = useState(String(tier.originalPrice));
  const [errors, setErrors] = useState<{
    name?: string;
    price?: string;
  }>({});
  const handleSubmit = () => {
    const errs: {
      name?: string;
      price?: string;
    } = {};
    if (!name.trim()) errs.name = 'Tier name is required';
    if (price === '' || isNaN(Number(price)) || Number(price) < 0) errs.price = 'Valid price is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSave({
      name: name.trim(),
      level,
      description: description.trim(),
      price: Number(price),
      originalPrice: Number(originalPrice) || Number(price)
    });
    onClose();
  };
  const isStandard = level === 'standard';
  return <ModalOverlay onClose={onClose} data-id="element-6330">
      <ModalHeader title={`Edit Tier — ${tier.name}`} onClose={onClose} data-id="element-6331" />
      <div className="px-6 py-5 space-y-4" data-id="element-6332">
        <div className="grid grid-cols-2 gap-3" data-id="element-6333">
          <Input label="Tier Name" value={name} onChange={e => {
          setName(e.target.value);
          setErrors(p => ({
            ...p,
            name: undefined
          }));
        }} error={errors.name} data-id="element-6334" />
          <div className="space-y-1.5" data-id="element-6335">
            <label className="block text-sm font-medium text-slate-700" data-id="element-6336">
              Level
            </label>
            <select className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" value={level} onChange={e => setLevel(e.target.value as PackageTier['level'])} data-id="element-6337">
              {levelOptions.map(l => <option key={l} value={l} data-id="element-6338">
                  {levelConfig[l]?.label || l}
                </option>)}
            </select>
          </div>
        </div>
        <div className="space-y-1.5" data-id="element-6339">
          <label className="block text-sm font-medium text-slate-700" data-id="element-6340">
            Description
          </label>
          <textarea className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400" rows={2} value={description} onChange={e => setDescription(e.target.value)} data-id="element-6341" />
        </div>
        {!isStandard && <div className="grid grid-cols-2 gap-3" data-id="element-6342">
            <Input label="Price ($)" type="number" value={price} onChange={e => {
          setPrice(e.target.value);
          setErrors(p => ({
            ...p,
            price: undefined
          }));
        }} error={errors.price} data-id="element-6343" />
            <Input label="Original Price ($)" type="number" value={originalPrice} onChange={e => setOriginalPrice(e.target.value)} data-id="element-6344" />
          </div>}
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50" data-id="element-6345">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-6346">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-6347">
          Save Changes
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Delete Confirm Modal ──────────────────────────────────────────────────────
function DeleteConfirmModal({
  title,
  message,
  onClose,
  onConfirm
}: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return <ModalOverlay onClose={onClose} data-id="element-6348">
      <ModalHeader title={title} onClose={onClose} data-id="element-6349" />
      <div className="px-6 py-5" data-id="element-6350">
        <p className="text-sm text-slate-600" data-id="element-6351">{message}</p>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50" data-id="element-6352">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-6353">
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={() => {
        onConfirm();
        onClose();
      }} data-id="element-6354">
          Delete
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Main Page ─────────────────────────────────────────────────────────────────
export function PackagesPage() {
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const bid = builderId || 'mattamy';
  const [packages, setPackages] = useState<BuilderPackage[]>(seedPackages);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPackageId, setExpandedPackageId] = useState<string | null>(seedPackages[0]?.id || null);
  const [activeTierIds, setActiveTierIds] = useState<Record<string, string>>({
    'pkg-1': 'tier-1'
  });
  // Modal state
  const [showCreatePackage, setShowCreatePackage] = useState(false);
  const [showEditPackage, setShowEditPackage] = useState<BuilderPackage | null>(null);
  const [showDeletePackage, setShowDeletePackage] = useState<BuilderPackage | null>(null);
  const [showAddTier, setShowAddTier] = useState<{
    packageId: string;
    packageName: string;
  } | null>(null);
  const [showEditTier, setShowEditTier] = useState<{
    packageId: string;
    tier: PackageTier;
  } | null>(null);
  const [showDeleteTier, setShowDeleteTier] = useState<{
    packageId: string;
    tier: PackageTier;
  } | null>(null);
  const [showAddOption, setShowAddOption] = useState<{
    packageId: string;
    tierId: string;
  } | null>(null);
  const [showDeleteOption, setShowDeleteOption] = useState<{
    packageId: string;
    tierId: string;
    option: PackageOption;
  } | null>(null);
  // ── Derived ──────────────────────────────────────────────────────────────
  const filteredPackages = useMemo(() => {
    let result = packages.filter(p => p.builderId === bid);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.tiers.some(t => t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)));
    }
    return result;
  }, [packages, searchQuery, bid]);
  const stats = useMemo(() => {
    const all = packages.filter(p => p.builderId === bid);
    return {
      total: all.length,
      active: all.filter(p => p.status === 'active').length,
      draft: all.filter(p => p.status === 'draft').length,
      totalTiers: all.reduce((acc, p) => acc + p.tiers.length, 0)
    };
  }, [packages, bid]);
  // ── Package Actions ───────────────────────────────────────────────────────
  const handleCreatePackage = (pkg: BuilderPackage) => {
    setPackages(prev => [...prev, pkg]);
    setExpandedPackageId(pkg.id);
  };
  const handleEditPackage = (id: string, updates: Partial<BuilderPackage>) => {
    setPackages(prev => prev.map(p => p.id === id ? {
      ...p,
      ...updates
    } : p));
  };
  const handleDeletePackage = (id: string) => {
    setPackages(prev => prev.filter(p => p.id !== id));
    if (expandedPackageId === id) setExpandedPackageId(null);
  };
  // ── Tier Actions ──────────────────────────────────────────────────────────
  const handleAddTier = (packageId: string, tierData: Omit<PackageTier, 'id' | 'options'>) => {
    const newTier: PackageTier = {
      ...tierData,
      id: genId(),
      options: []
    };
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: [...p.tiers, newTier]
    } : p));
    setActiveTierIds(prev => ({
      ...prev,
      [packageId]: newTier.id
    }));
  };
  const handleEditTier = (packageId: string, tierId: string, updates: Partial<PackageTier>) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.map(t => t.id === tierId ? {
        ...t,
        ...updates
      } : t)
    } : p));
  };
  const handleDeleteTier = (packageId: string, tierId: string) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.filter(t => t.id !== tierId)
    } : p));
    setActiveTierIds(prev => {
      const next = {
        ...prev
      };
      if (next[packageId] === tierId) delete next[packageId];
      return next;
    });
  };
  const handleDuplicateTier = (packageId: string, tier: PackageTier) => {
    const newTier: PackageTier = {
      ...tier,
      id: genId(),
      name: `${tier.name} (Copy)`,
      options: tier.options.map(opt => ({
        ...opt,
        id: genId()
      }))
    };
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: [...p.tiers, newTier]
    } : p));
    setActiveTierIds(prev => ({
      ...prev,
      [packageId]: newTier.id
    }));
  };
  // ── Option Actions ────────────────────────────────────────────────────────
  const handleAddOption = (packageId: string, tierId: string, optData: Omit<PackageOption, 'id'>) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.map(t => t.id === tierId ? {
        ...t,
        options: [...t.options, {
          ...optData,
          id: genId()
        }]
      } : t)
    } : p));
  };
  const handleAddOptionFromGlobal = (packageId: string, tierId: string, globalOption: GlobalOption) => {
    handleAddOption(packageId, tierId, {
      name: globalOption.name,
      category: globalOption.subcategory || globalOption.category,
      image: globalOption.imageUrl || ''
    });
  };
  const handleReorderOption = (packageId: string, tierId: string, optionIndex: number, direction: 'left' | 'right') => {
    setPackages(prev => prev.map(p => {
      if (p.id !== packageId) return p;
      return {
        ...p,
        tiers: p.tiers.map(t => {
          if (t.id !== tierId) return t;
          const newOptions = [...t.options];
          const swapIndex = direction === 'left' ? optionIndex - 1 : optionIndex + 1;
          if (swapIndex >= 0 && swapIndex < newOptions.length) {
            const temp = newOptions[optionIndex];
            newOptions[optionIndex] = newOptions[swapIndex];
            newOptions[swapIndex] = temp;
          }
          return {
            ...t,
            options: newOptions
          };
        })
      };
    }));
  };
  // ── Navigation ────────────────────────────────────────────────────────────
  const handleTogglePackage = (pkgId: string) => {
    setExpandedPackageId(expandedPackageId === pkgId ? null : pkgId);
    if (!activeTierIds[pkgId]) {
      const pkg = filteredPackages.find(p => p.id === pkgId);
      if (pkg && pkg.tiers.length > 0) {
        const firstUpgrade = pkg.tiers.find(t => t.price > 0);
        setActiveTierIds(prev => ({
          ...prev,
          [pkgId]: firstUpgrade ? firstUpgrade.id : pkg.tiers[0].id
        }));
      }
    }
  };
  const setActiveTier = (pkgId: string, tierId: string) => {
    setActiveTierIds(prev => ({
      ...prev,
      [pkgId]: tierId
    }));
  };
  return <div className="space-y-6 max-w-5xl mx-auto" data-id="element-6355">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-6356">
        <div data-id="element-6357">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-6358">Packages</h1>
          <p className="text-slate-500 mt-1" data-id="element-6359">
            Create and manage curated option bundles for buyers
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" data-id="element-6361" />} onClick={() => setShowCreatePackage(true)} data-id="element-6360">
          Create Package
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3" data-id="element-6362">
        <Card noPadding data-id="element-6363">
          <div className="p-4" data-id="element-6364">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6365">
              Total Packages
            </p>
            <p className="text-2xl font-bold text-slate-900" data-id="element-6366">{stats.total}</p>
          </div>
        </Card>
        <Card noPadding data-id="element-6367">
          <div className="p-4" data-id="element-6368">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6369">
              Active
            </p>
            <p className="text-2xl font-bold text-emerald-600" data-id="element-6370">
              {stats.active}
            </p>
          </div>
        </Card>
        <Card noPadding data-id="element-6371">
          <div className="p-4" data-id="element-6372">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6373">
              Draft
            </p>
            <p className="text-2xl font-bold text-slate-500" data-id="element-6374">{stats.draft}</p>
          </div>
        </Card>
        <Card noPadding data-id="element-6375">
          <div className="p-4" data-id="element-6376">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6377">
              Total Tiers
            </p>
            <p className="text-2xl font-bold text-primary-600" data-id="element-6378">
              {stats.totalTiers}
            </p>
          </div>
        </Card>
      </div>

      {/* Search */}
      <Card noPadding data-id="element-6379">
        <div className="p-4 bg-slate-50 rounded-xl" data-id="element-6380">
          <div className="relative" data-id="element-6381">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-6382" />
            <input type="text" placeholder="Search packages or tiers..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6383" />
          </div>
        </div>
      </Card>

      {/* Packages */}
      <div className="space-y-4" data-id="element-6384">
        {filteredPackages.length === 0 ? <Card data-id="element-6385">
            <CardContent className="py-16 text-center" data-id="element-6386">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-6387" />
              <h2 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-6388">
                No packages found
              </h2>
              <p className="text-slate-500 mb-4" data-id="element-6389">
                Try adjusting your search or create a new package.
              </p>
              <Button leftIcon={<Plus className="h-4 w-4" data-id="element-6391" />} size="sm" onClick={() => setShowCreatePackage(true)} data-id="element-6390">
                Create Package
              </Button>
            </CardContent>
          </Card> : filteredPackages.map(pkg => {
        const isExpanded = expandedPackageId === pkg.id;
        const StatusIcon = statusConfig[pkg.status].icon;
        const activeTierId = activeTierIds[pkg.id];
        const activeTier = pkg.tiers.find(t => t.id === activeTierId);
        const levelInfo = activeTier ? levelConfig[activeTier.level] : null;
        return <Card key={pkg.id} noPadding className={`transition-all ${isExpanded ? 'ring-2 ring-primary-200' : ''}`} data-id="element-6392">
                {/* Package row header */}
                <div className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50/50 rounded-xl transition-colors" onClick={() => handleTogglePackage(pkg.id)} data-id="element-6393">
                  <div className="flex items-center gap-3" data-id="element-6394">
                    <div className="text-slate-400" data-id="element-6395">
                      {isExpanded ? <ChevronDown className="h-4 w-4" data-id="element-6396" /> : <ChevronRight className="h-4 w-4" data-id="element-6397" />}
                    </div>
                    <div className="h-9 w-9 rounded-lg bg-primary-50 border border-primary-100 flex items-center justify-center flex-shrink-0" data-id="element-6398">
                      <Layers className="h-4 w-4 text-primary-600" data-id="element-6399" />
                    </div>
                    <div data-id="element-6400">
                      <div className="flex items-center gap-2" data-id="element-6401">
                        <span className="font-semibold text-slate-900" data-id="element-6402">
                          {pkg.name}
                        </span>
                        <Badge variant={statusConfig[pkg.status].variant} className="text-[11px]" data-id="element-6403">
                          <StatusIcon className="h-3 w-3 mr-1" data-id="element-6404" />
                          {statusConfig[pkg.status].label}
                        </Badge>
                        {pkg.scope === 'global' ? <Badge variant="neutral" className="text-[11px] bg-violet-50 text-violet-600 border-violet-200" data-id="element-6405">
                            <Globe className="h-3 w-3 mr-1" data-id="element-6406" />
                            Global
                          </Badge> : pkg.room ? <Badge variant="neutral" className="text-[11px] bg-slate-100 text-slate-600 border-slate-200" data-id="element-6407">
                            {pkg.room}
                          </Badge> : null}
                        <Badge variant="neutral" className={`text-[11px] ${pkg.allowCustomization ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`} data-id="element-6408">
                          {pkg.allowCustomization ? <Unlock className="h-3 w-3 mr-1" data-id="element-6409" /> : <Lock className="h-3 w-3 mr-1" data-id="element-6410" />}
                          {pkg.allowCustomization ? 'Customizable' : 'Locked'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-6411">
                        {pkg.tiers.length} tiers ·{' '}
                        {pkg.tiers.reduce((a, t) => a + t.options.length, 0)}{' '}
                        options
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()} data-id="element-6412">
                    <Button variant="ghost" size="sm" onClick={() => setShowEditPackage(pkg)} data-id="element-6413">
                      <Edit className="h-3.5 w-3.5 mr-1" data-id="element-6414" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowDeletePackage(pkg)} data-id="element-6415">
                      <Trash2 className="h-3.5 w-3.5 text-red-400" data-id="element-6416" />
                    </Button>
                  </div>
                </div>

                {/* Expanded tier detail */}
                {isExpanded && <div className="border-t border-slate-100 px-5 pb-5 pt-4" data-id="element-6417">
                    {/* Tier tabs */}
                    <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1" data-id="element-6418">
                      {pkg.tiers.map(tier => {
                const tLevelInfo = levelConfig[tier.level];
                const isActive = activeTierId === tier.id;
                return <button key={tier.id} onClick={() => setActiveTier(pkg.id, tier.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${isActive ? 'bg-primary-500 text-white border-primary-500 shadow-sm' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`} data-id="element-6419">
                            {tier.level !== 'standard' && <span className={`inline-block h-2 w-2 rounded-full ${isActive ? 'bg-white/70' : 'bg-slate-300'}`} data-id="element-6420" />}
                            {tier.level === 'standard' && <Star className={`h-3 w-3 ${isActive ? 'text-white/80' : 'text-emerald-500'}`} data-id="element-6421" />}
                            {tier.name}
                          </button>;
              })}
                      <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary-300 hover:text-primary-500 transition-all" onClick={() => setShowAddTier({
                packageId: pkg.id,
                packageName: pkg.name
              })} data-id="element-6422">
                        <Plus className="h-3.5 w-3.5" data-id="element-6423" /> Add Tier
                      </button>
                    </div>

                    {/* Active tier content */}
                    {activeTier && <div data-id="element-6424">
                        {/* Tier meta row */}
                        <div className="flex items-start justify-between gap-4 mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-6425">
                          <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-6426">
                            {levelInfo && <span className={`px-2 py-0.5 rounded text-xs font-semibold border flex-shrink-0 mt-0.5 ${levelInfo.bgColor} ${levelInfo.color} ${levelInfo.borderColor}`} data-id="element-6427">
                                {levelInfo.label}
                              </span>}
                            <div className="flex-1 min-w-0" data-id="element-6428">
                              <p className="text-sm font-medium text-slate-900" data-id="element-6429">
                                {activeTier.description || 'No description'}
                              </p>
                              {activeTier.price > 0 ? <div className="flex items-center gap-2 mt-1" data-id="element-6430">
                                  <span className="text-base font-bold text-slate-900" data-id="element-6431">
                                    ${activeTier.price.toLocaleString()}
                                  </span>
                                  {activeTier.originalPrice > activeTier.price && <>
                                      <span className="text-sm text-slate-400 line-through" data-id="element-6432">
                                        $
                                        {activeTier.originalPrice.toLocaleString()}
                                      </span>
                                      <span className="text-xs font-medium text-emerald-600" data-id="element-6433">
                                        Save $
                                        {(activeTier.originalPrice - activeTier.price).toLocaleString()}
                                      </span>
                                    </>}
                                </div> : <p className="text-sm font-medium text-emerald-600 mt-1" data-id="element-6434">
                                  Included
                                </p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-6435">
                            <Button variant="outline" size="sm" leftIcon={<Copy className="h-3.5 w-3.5" data-id="element-6437" />} onClick={() => handleDuplicateTier(pkg.id, activeTier)} data-id="element-6436">
                              Duplicate
                            </Button>
                            <Button variant="outline" size="sm" leftIcon={<Edit className="h-3.5 w-3.5" data-id="element-6439" />} onClick={() => setShowEditTier({
                    packageId: pkg.id,
                    tier: activeTier
                  })} data-id="element-6438">
                              Edit Tier
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setShowDeleteTier({
                    packageId: pkg.id,
                    tier: activeTier
                  })} data-id="element-6440">
                              <Trash2 className="h-3.5 w-3.5 text-red-400" data-id="element-6441" />
                            </Button>
                          </div>
                        </div>

                        {/* Options grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-id="element-6442">
                          {activeTier.options.map((opt, idx) => <div key={opt.id} className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 hover:shadow-sm transition-all" data-id="element-6443">
                              <div className="h-16 bg-slate-100 flex items-center justify-center overflow-hidden" data-id="element-6444">
                                {opt.image ? <img src={opt.image} alt={opt.name} className="h-full w-full object-cover" data-id="element-6445" /> : <ImageOff className="h-5 w-5 text-slate-300" data-id="element-6446" />}
                              </div>
                              <div className="p-2" data-id="element-6447">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider truncate" data-id="element-6448">
                                  {opt.category}
                                </p>
                                <p className="text-xs font-medium text-slate-700 truncate mt-0.5" data-id="element-6449">
                                  {opt.name}
                                </p>
                              </div>
                              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5" data-id="element-6450">
                                {idx > 0 && <button className="p-1 bg-white rounded shadow-sm border border-slate-200 text-slate-400 hover:text-primary-500 transition-colors" onClick={() => handleReorderOption(pkg.id, activeTier.id, idx, 'left')} data-id="element-6451">
                                    <ChevronLeft className="h-3 w-3" data-id="element-6452" />
                                  </button>}
                                {idx < activeTier.options.length - 1 && <button className="p-1 bg-white rounded shadow-sm border border-slate-200 text-slate-400 hover:text-primary-500 transition-colors" onClick={() => handleReorderOption(pkg.id, activeTier.id, idx, 'right')} data-id="element-6453">
                                    <ChevronRight className="h-3 w-3" data-id="element-6454" />
                                  </button>}
                                <button className="p-1 bg-white rounded shadow-sm border border-slate-200 text-slate-400 hover:text-red-500 transition-colors ml-0.5" onClick={() => setShowDeleteOption({
                      packageId: pkg.id,
                      tierId: activeTier.id,
                      option: opt
                    })} data-id="element-6455">
                                  <X className="h-3 w-3" data-id="element-6456" />
                                </button>
                              </div>
                            </div>)}
                          {/* Add option */}
                          <button className="h-full min-h-[88px] border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center gap-1 text-slate-400 hover:border-primary-300 hover:text-primary-500 transition-all" onClick={() => setShowAddOption({
                  packageId: pkg.id,
                  tierId: activeTier.id
                })} data-id="element-6457">
                            <Plus className="h-5 w-5" data-id="element-6458" />
                            <span className="text-[11px] font-medium" data-id="element-6459">
                              Add Option
                            </span>
                          </button>
                        </div>
                      </div>}

                    {pkg.tiers.length === 0 && <div className="text-center py-8 text-slate-400" data-id="element-6460">
                        <Package className="h-8 w-8 mx-auto mb-2 text-slate-300" data-id="element-6461" />
                        <p className="text-sm" data-id="element-6462">
                          No tiers yet. Add your first tier above.
                        </p>
                      </div>}
                  </div>}
              </Card>;
      })}
      </div>

      {/* ── Modals ── */}

      {showCreatePackage && <CreatePackageModal builderId={bid} onClose={() => setShowCreatePackage(false)} onCreate={handleCreatePackage} data-id="element-6463" />}

      {showEditPackage && <EditPackageModal pkg={showEditPackage} onClose={() => setShowEditPackage(null)} onSave={updates => handleEditPackage(showEditPackage.id, updates)} data-id="element-6464" />}

      {showDeletePackage && <DeleteConfirmModal title="Delete Package" message={`Are you sure you want to delete "${showDeletePackage.name}"? All tiers and options will be removed.`} onClose={() => setShowDeletePackage(null)} onConfirm={() => handleDeletePackage(showDeletePackage.id)} data-id="element-6465" />}

      {showAddTier && <AddTierModal packageName={showAddTier.packageName} onClose={() => setShowAddTier(null)} onAdd={tierData => handleAddTier(showAddTier.packageId, tierData)} data-id="element-6466" />}

      {showEditTier && <EditTierModal tier={showEditTier.tier} onClose={() => setShowEditTier(null)} onSave={updates => handleEditTier(showEditTier.packageId, showEditTier.tier.id, updates)} data-id="element-6467" />}

      {showDeleteTier && <DeleteConfirmModal title="Delete Tier" message={`Delete the "${showDeleteTier.tier.name}" tier? All options within it will be removed.`} onClose={() => setShowDeleteTier(null)} onConfirm={() => handleDeleteTier(showDeleteTier.packageId, showDeleteTier.tier.id)} data-id="element-6468" />}

      {showAddOption && <CreateOptionModal isOpen={true} onClose={() => setShowAddOption(null)} existingOptions={initialGlobalOptions} onCreateNew={globalOption => {
      handleAddOptionFromGlobal(showAddOption.packageId, showAddOption.tierId, globalOption);
      setShowAddOption(null);
    }} onLinkExisting={globalOption => {
      handleAddOptionFromGlobal(showAddOption.packageId, showAddOption.tierId, globalOption);
      setShowAddOption(null);
    }} breadcrumbs={[]} catalogs={initialCatalogs} data-id="element-6469" />}

      {showDeleteOption && <DeleteConfirmModal title="Remove Option" message={`Remove "${showDeleteOption.option.name}" from this tier?`} onClose={() => setShowDeleteOption(null)} onConfirm={() => handleDeleteOption(showDeleteOption.packageId, showDeleteOption.tierId, showDeleteOption.option.id)} data-id="element-6470" />}
    </div>;
}